import styled from "@emotion/styled";
import type { FC } from "react";
import { useEffect } from "react";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import FaqComponent from "src/ui/components/FaqComponent/FaqComponent";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import Translate from "src/ui/components/Translate/Translate";
import logo from "src/ui/assets/icons/9am-logo.svg";
import translate from "src/lib/translate";
import clsx from "clsx";
import { useBloc } from "src/state/state";
import { KnownProgram } from "src/constants/programs";
import { useBlocNext } from "@blac/react";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { APP_BREAKPOINT } from "src/constants/layout";
import ProgramBloc, { ProgramStatus } from "src/state/ProgramBloc/ProgramBloc";

const CONTENT_MAX_WIDTH = 410;

const ContentMaxWidth = styled.div`
  max-width: ${CONTENT_MAX_WIDTH}px;
  margin: 0 auto;
`;

const GradientContainer = styled.div`
  position: relative;
  z-index: 1;
  margin-top: calc(var(--padding-top) * -1);
  margin-right: calc(var(--padding-end) * -1);
  margin-left: calc(var(--padding-start) * -1);
  padding-top: 2rem;
  padding-right: 1.5rem;
  padding-left: 1.5rem;
  padding-bottom: 2rem;
  background: var(--gradient-sunrise-light-vertical);
  background-size: 100% 200%;
  background-position-y: center;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    margin: 0 auto;
    max-width: ${CONTENT_MAX_WIDTH}px;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 2rem 1.5rem;
  }

  &.with-partner-session-styles {
    background: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;

  img {
    width: 4rem;
    height: 4rem;
    border: 2px solid white;
    border-radius: 50%;
  }
`;

const Title = styled.h4`
  text-align: center;
  text-wrap: pretty;
`;

const Description = styled.p`
  text-align: center;
`;

const RulesCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 3rem;
  box-shadow:
    0 0 0 0 rgba(69, 49, 22, 0.08),
    -5px 5px 16px 0 rgba(69, 49, 22, 0.08),
    -20px 20px 29px 0 rgba(69, 49, 22, 0.07),
    -46px 45px 39px 0 rgba(69, 49, 22, 0.03),
    -82px 81px 46px 0 rgba(69, 49, 22, 0.01);
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    gap: 1rem;
    padding: 1.5rem 1rem 1.5rem 1.5rem;
  }
`;

const ItemDivider = styled.div`
  border-bottom: 1px solid var(--color-cream-dark);
  margin: 0 1rem;
`;

const EmojiCircle = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: linear-gradient(229deg, #ffdfb4 18%, #fddee9 34%, #c1d7fb 82%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.25rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    width: 3.375rem;
    height: 3.375rem;
    font-size: 1.5rem;
  }
`;

const BottomCreamGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 7rem;
  background: linear-gradient(to bottom, transparent, var(--color-cream));
  z-index: -1;
`;

const Content = styled.div`
  flex: 1;
`;

const ItemTitle = styled.h5`
  margin: 0 0 0.125rem 0;
  color: var(--color-charcoal);
`;

const ItemText = styled.p`
  margin: 0;
`;

const ConsistencyScoreInfoPopup: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [{ partnerStyles }] = useBlocNext(AppViewCubit);
  const [programState, programBloc] = useBloc(ProgramBloc);

  const shouldSeeHWJContent =
    programState.autoProgramDetected ===
    KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W;

  useEffect(() => {
    void programBloc.checkUserProfileProgramData();
  }, [shouldSeeHWJContent]);

  const areModulesComplete =
    !shouldSeeHWJContent ||
    programState.programStatus[programState.autoProgramDetected ?? ""] ===
      ProgramStatus.COMPLETED;

  const vitalsTitleKey = shouldSeeHWJContent
    ? "consistency_score.info.vitals_tracking.title"
    : "consistency_score.info.vitals_tracking.title_non_hwj";

  const vitalsDescriptionKey = shouldSeeHWJContent
    ? "consistency_score.info.vitals_tracking.description"
    : "consistency_score.info.vitals_tracking.description_non_hwj";

  const programTitleKey = shouldSeeHWJContent
    ? areModulesComplete
      ? "consistency_score.info.program.title_after"
      : "consistency_score.info.program.title"
    : "consistency_score.info.program.title_non_hwj";

  const programDescriptionKey = shouldSeeHWJContent
    ? areModulesComplete
      ? "consistency_score.info.program.description_after"
      : "consistency_score.info.program.description"
    : "consistency_score.info.program.description_non_hwj";

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  return (
    <InDialog
      popup
      title={translate("consistency_score.info.title")}
      onClose={onClose}
      width="calc(100vw - 2rem)"
      height="calc(100vh - 2rem)"
      returnUrl={returnUrl}
    >
      <GradientContainer
        className={clsx({
          "with-partner-session-styles": partnerStyles
        })}
      >
        <ContentMaxWidth>
          <LogoContainer>
            <img src={logo} alt="" aria-hidden="true" />
          </LogoContainer>

          <Title>
            <Translate msg="consistency_score.info.title" />
          </Title>

          <Description className="as-body">
            <Translate msg="consistency_score.info.description" />
          </Description>
        </ContentMaxWidth>
        {!partnerStyles && <BottomCreamGradient />}
      </GradientContainer>

      <ContentMaxWidth>
        <RulesCard>
          <ItemsList>
            <Item>
              <EmojiCircle>🎬</EmojiCircle>
              <Content>
                <ItemTitle className="as-body2">
                  <Translate msg="consistency_score.info.start_fresh.title" />
                </ItemTitle>
                <ItemText className="as-little">
                  <Translate msg="consistency_score.info.start_fresh.description" />
                </ItemText>
              </Content>
            </Item>

            <ItemDivider />

            <Item>
              <EmojiCircle>{shouldSeeHWJContent ? "⚖️" : "🔍"}</EmojiCircle>
              <Content>
                <ItemTitle className="as-body2">
                  <Translate msg={vitalsTitleKey} />
                </ItemTitle>
                <ItemText className="as-little">
                  <Translate msg={vitalsDescriptionKey} />
                </ItemText>
              </Content>
            </Item>

            <ItemDivider />

            <Item>
              <EmojiCircle>📋</EmojiCircle>
              <Content>
                <ItemTitle className="as-body2">
                  <Translate msg={programTitleKey} />
                </ItemTitle>
                <ItemText className="as-little">
                  <Translate msg={programDescriptionKey} />
                </ItemText>
              </Content>
            </Item>
          </ItemsList>
        </RulesCard>

        <FaqComponent
          context={
            shouldSeeHWJContent
              ? "consistencyScoreHWJ"
              : "consistencyScoreNonHWJ"
          }
        />
      </ContentMaxWidth>
    </InDialog>
  );
};

export default ConsistencyScoreInfoPopup;
